import { useCallback, useMemo, useRef, useState } from 'react'

export type CarouselControllerHandle = {
  scrollToIndex: (index: number) => void
  scrollToNext: () => void
  scrollToPrevious: () => void
}

export type CarouselControllerRef = React.RefObject<CarouselControllerHandle>

type CarouselState = {
  currentIndex: number
  canScrollPrevious: boolean
  canScrollNext: boolean
}

export type CarouselRef = {
  ref: CarouselControllerRef
  _updateState: (state: CarouselState) => void
}

export type CarouselController = {
  carouselRef: CarouselRef
  currentIndex: number
  canScrollPrevious: boolean
  canScrollNext: boolean
  scrollToIndex: (index: number) => void
  scrollToNext: () => void
  scrollToPrevious: () => void
}

export const useCarouselController = (): CarouselController => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [canScrollPrevious, setCanScrollPrevious] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(true)
  const ref = useRef<CarouselControllerHandle>(null)

  const scrollToIndex = useCallback((index: number) => {
    ref.current?.scrollToIndex(index)
  }, [])

  const scrollToNext = useCallback(() => {
    ref.current?.scrollToNext()
  }, [])

  const scrollToPrevious = useCallback(() => {
    ref.current?.scrollToPrevious()
  }, [])

  const _updateState = useCallback((state: CarouselState) => {
    setCurrentIndex(state.currentIndex)
    setCanScrollPrevious(state.canScrollPrevious)
    setCanScrollNext(state.canScrollNext)
  }, [])

  const carouselRef = useMemo(() => ({ ref, _updateState }), [_updateState])

  return {
    carouselRef,
    currentIndex,
    canScrollPrevious,
    canScrollNext,
    scrollToIndex,
    scrollToNext,
    scrollToPrevious,
  }
}
